import { Flex, Card, Form, Typography, Input, Button } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import type { GetProps } from 'antd';
import {
	LockOutlined,
	MailOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import { API_URL } from "env";

type OTPProps = GetProps<typeof Input.OTP>;

const { Text } = Typography;

function ForgetPassword() {
	const navigate = useNavigate();
	const [email, setEmail] = useState('');
	const [otp, setOtp] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [resetToken, setResetToken] = useState('');

	const [step, setStep] = useState(0);

	// UI function
	function nextStep() {
		setStep(step + 1);
	}

	function prevStep() {
		setStep(step - 1);
	}

	function checkStep(step: number) {
		if (step === 0) {
			if (email) {
				return true;
			} else {
				return false;
			}
		} else if (step === 1) {
			if (otp && otp.length === 6) {
				return true;
			}
			else if (password && password.length >= 6) {
				return true;
			} else {
				return false;
			}
		}
	}

	// API function

	async function submitResetPasswordEmail() {
		try {
			const res = await fetch(API_URL + '/api/v1/forget-password', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email }),
			});
			const data = await res.json();
			if (data.status) {
				toast.success("Password reset email sent. Please check your email.");
				nextStep();
			}
			else {
				toast.error("Error resetting password. Please try again. Error:" + data.message);
			}
		}
		catch (error) {
			toast.error("Error resetting password. Please try again. Error:" + error);
		}
	}

	async function submitResendOTP() {
		try {
			const res = await fetch(API_URL + '/api/v1/resend-forget-password-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email }),
			});
			const data = await res.json();
			if (data.status) {
				toast.success("OTP resent successfully. Please check your email.");
			}
			else {
				toast.error("Error resending OTP. Please try again. Error:" + data.message);
			}
		}
		catch (error) {
			toast.error("Error resending OTP. Please try again. Error:" + error);
		}
	}

	async function submitVerifyOTP(otpValue:string) {
		try {
			const res = await fetch(API_URL + '/api/v1/verify-reset-password-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email, otp: otpValue}),
			});
			const data = await res.json();
			if (data.status) {
				setResetToken(data.reset_token);
				toast.success("OTP verified successfully. Please enter your new password.");
				nextStep();
			}
			else {
				toast.error("Error verifying OTP. Please try again. Error:" + data.message);
			}
		}
		catch (error) {
			toast.error("Error verifying OTP. Please try again. Error:" + error);
		}
	}

	async function submitReset() {
		try {
			const res = await fetch(API_URL + '/api/v1/reset-password-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email,
					reset_token: resetToken,
					password,
					password_confirmation: confirmPassword
				}),
			});
			const data = await res.json();
			if (data.status) {
				toast.success("Password reset successful. Redirecting to login page in 3 seconds...");
				setTimeout(() => {
					navigate('/sign-in');
				}, 3000);
			}
			else {
				toast.error("Error resetting password. Please try again. Error:" + data.message);
			}
		}
		catch (error) {
			toast.error("Error resetting password. Please try again. Error:" + error);
		}
	}


	return (
		<div className="Login-page">
			<div className="fixed inset-0 w-full h-full -z-20">
				<video
					autoPlay
					loop
					muted
					playsInline
					className="w-full h-full object-cover"
				>
					<source src="/video/new-bg-video.mp4" type="video/mp4" />
					{/* <source src="/video/bg-video.webm" type="video/webm" /> */}
					Your browser does not support the video tag.
				</video>
			</div>
			{/* Video Overlay */}
			<div className="fixed inset-0 bg-white/10 -z-10"></div>

			<div className='absolute top-4 left-4 cursor-pointer w-38 h-12 md:w-48 md:h-22' onClick={() => navigate('/')}>
				<img className='' src='./images/logo2.png' alt='logo' />
			</div>
			{
				step === 0 ? (
					<Card className="card-custom card-custom-forget !bg-transparent !bg-white/5 !backdrop-blur-lg">
						<Typography.Title level={2} className="text-center !text-white mb-6">
							Forgot Password?
						</Typography.Title>
						<Text className="block text-center !text-white mb-6 w-fit mx-auto">
							Submit your email and follow instructions
						</Text>
						<Form
							name='forgetpass-form'
							layout="vertical"
							initialValues={{ remember: true }}
							size='large'
						>
							<Form.Item>
								<Input
									placeholder="Enter email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</Form.Item>

							<Form.Item>
								<Button onClick={
									() => {
										if (checkStep(0)) {
											submitResetPasswordEmail();
										}
									}
								} type="primary" htmlType="submit" block className="h-10 rounded-lg bg-blue-600 hover:bg-blue-700">
									Reset Password
								</Button>
							</Form.Item>
						</Form>
					</Card>
				) : null
			}
			{
				step === 1 ? (
					<Card className="card-custom card-custom-forget !bg-transparent !bg-white/5 !backdrop-blur-lg">
						<Typography.Title level={2} className="text-center !text-white mb-6">
							Check Your Email
						</Typography.Title>
						<Text className="block text-center !text-white mb-6 w-fit mx-auto sub-title">
							We’ve sent instructions on how to reset your password to <strong>{email}</strong>
						</Text>
						<Form
							name='forgetpass-form'
							layout="vertical"
							initialValues={{ remember: true }}
							size='large'
						>

							<Flex justify="center" align="center" className="mb-4" vertical>
								<Form.Item className="w-full mb-4">
									<h4 className="!text-lg !text-white text-center mb-2">Enter 6-digit code</h4>
									<Flex justify="center">
										<Input.OTP
											value={otp}
											onChange={(value: OTPProps['value']) => {
												if (value !== undefined) {
													setOtp(value.toString());
													if (value.toString().length === 6) {
														submitVerifyOTP(value);
													}
												}
											}}
											autoFocus
											length={6}
											className="otp-input"
										/>
									</Flex>
								</Form.Item>
							</Flex>
						</Form>
						<Button onClick={prevStep} type="link" className="!text-white mb-4">
							&larr; Back
						</Button>
						<Text italic className="block text-center !text-white mb-6 w-fit mx-auto">
							Email not received?&nbsp;
							<Button type="link" className='!text-blue-500 p-0 m-0' onClick={submitResendOTP}>Resend</Button>
						</Text>
					</Card>
				) : null
			}
			{
				step === 2 ? (
					<Card className="card-custom card-custom-forget !bg-transparent !bg-white/5 !backdrop-blur-lg">
						<Form
							name='forgetpass-form'
							layout="vertical"
							initialValues={{ remember: true }}
							size='large'
						>

							<Flex justify="center" align="center" className="mb-4" vertical>
								<Form.Item
									name="password"
									rules={[
										{ required: true, message: "Password is required!" },
										{ min: 8, message: "Password must be at least 8 characters!" },
									]}
									className="w-full mb-4"
								>
									<Input.Password
										prefix={<LockOutlined className="text-white" />}
										className="custom-input !bg-transparent !text-white"
										placeholder="Enter new password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
									/>
								</Form.Item>
								<Form.Item
									name="confirm_password"
									dependencies={['password']}
									rules={[
										{ required: true, message: "Confirm Password is required!" },
										{ min: 8, message: "Password must be at least 8 characters!" },
										({ getFieldValue }) => ({
											validator(_, value) {
												if (!value || getFieldValue("password") === value) {
													return Promise.resolve();
												}
												return Promise.reject(new Error("Passwords do not match!"));
											},
										}),
									]}
									className="w-full mb-4"
								>
									<Input.Password
										prefix={<LockOutlined className="text-white" />}
										className="custom-input !bg-transparent !text-white"
										placeholder="Confirm new password"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
									/>
								</Form.Item>

								<Form.Item className="w-full">
									<Button disabled={!checkStep(1)} onClick={
										() => {
											if (checkStep(1)) {
												submitReset();
											}
										}
									} type="primary" htmlType="submit" block className="h-10  rounded-lg bg-blue-600 hover:bg-blue-700">
										Set New Password
									</Button>
								</Form.Item>
							</Flex>
						</Form>
					</Card>
				) : null
			}
			<ToastContainer />
		</div>
	);
}

export default ForgetPassword;
